import { useEffect, useRef, useState } from 'react';
import { useDebounceCallback } from './use-debounce-callback';

type DebouncedResult<T> = {
  /** Latest settled value. Kept while a newer input is still pending. */
  result: T;
  /** True while the current input has not yet produced a settled value. */
  isPending: boolean;
  /** True when the latest settle for the current input rejected. */
  failed: boolean;
};

type UseDebouncedResultOptions = {
  /** Skip evaluation (e.g. empty input): result resets to idle, isPending stays false. */
  skip?: boolean;
  debounceMs?: number;
};

/**
 * Debounces an async computation of `key`'s inputs and exposes the latest
 * settled value with stale-result safety.
 *
 * `isPending` is derived during render from `key !== <settled key>`, so it flips
 * true the moment the inputs change — before the debounce even fires. Callers
 * can gate on it (e.g. don't act on a value while a newer one is pending)
 * without ever reading a stale settle.
 *
 * Each request gets a monotonic id, and only the latest may settle — so a
 * superseded in-flight run is dropped even when it re-uses the same key.
 * Comparing by key alone would let a slow earlier run land after a newer one
 * and overwrite it.
 */
export function useDebouncedResult<T>(
  key: string,
  compute: () => Promise<T>,
  idle: T,
  { skip = false, debounceMs = 0 }: UseDebouncedResultOptions = {},
): DebouncedResult<T> {
  const [settled, setSettled] = useState<{ key: string; result: T; failed: boolean }>({
    key: '',
    result: idle,
    failed: false,
  });

  // Read the latest compute closure without making its identity a dependency.
  const computeRef = useRef(compute);
  computeRef.current = compute;

  // Identifies each distinct request; only the latest may settle.
  const requestIdRef = useRef(0);

  const run = useDebounceCallback((requestId: number, requestedKey: string) => {
    void computeRef
      .current()
      .then((result) => {
        if (requestId === requestIdRef.current) {
          setSettled({ key: requestedKey, result, failed: false });
        }
      })
      .catch(() => {
        if (requestId === requestIdRef.current) {
          setSettled({ key: requestedKey, result: idle, failed: true });
        }
      });
  }, debounceMs);

  useEffect(() => {
    // Bump on every run — including a skip — so any in-flight request is
    // superseded, regardless of whether the key changed.
    const requestId = (requestIdRef.current += 1);
    if (skip) {
      setSettled({ key, result: idle, failed: false });
      return;
    }
    run(requestId, key);
  }, [key, skip, idle, run]);

  const isPending = !skip && key !== settled.key;
  // When skipped, ignore any previously-settled value so a stale result can't
  // survive the input being cleared before the effect resets `settled`.
  const result = skip ? idle : settled.result;
  return { result, isPending, failed: !skip && settled.failed && !isPending };
}
